import { queryClient } from "@/ajax/queryClient";
import Chat from "@/features/chat/Chat";
import { useProviderUser } from "@/hooks/useProviderUser";
import { useTRPC, useTRPCClient } from "@/trpc";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { z } from "zod";
import { useState, useEffect, useMemo, useCallback } from "react";

const PAGE_SIZE = 50;
export const Route = createFileRoute("/_provider/provider/chat/")({
  component: ProviderChatRoute,

  validateSearch: z.object({
    thread: z.string().optional(),
    archived: z.boolean().optional(),
    unreadOnly: z.boolean().optional(),
  }),
});

function ProviderChatRoute() {
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const router = useRouter();
  const providerUser = useProviderUser();
  const search = Route.useSearch();
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const queryParams = useMemo(
    () => ({
      isArchived: search.archived === true,
      search: searchQuery || undefined,
      unreadOnly: search.unreadOnly === true ? true : undefined,
    }),
    [search.archived, search.unreadOnly, searchQuery],
  );

  const { data: selectedThreadData, isLoading: isLoadingSelectedThread } =
    useQuery(
      trpc.chat.providerGetSingleThread.queryOptions(
        {
          threadId: search.thread!,
          isArchived: search.archived === true,
        },
        {
          enabled: !!search.thread,
        },
      ),
    );

  const {
    data: infiniteData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["providerChatThreadsInfinite", queryParams],
    queryFn: ({ pageParam = 0 }) =>
      trpcClient.chat.providerListChatThreads.query({
        ...queryParams,
        offset: pageParam * PAGE_SIZE,
        limit: PAGE_SIZE,
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (
        typeof lastPage === "object" &&
        "hasMore" in lastPage &&
        lastPage.hasMore
      ) {
        return allPages.length;
      }
      if (Array.isArray(lastPage) && lastPage.length === PAGE_SIZE) {
        return allPages.length;
      }
      return undefined;
    },
    initialPageParam: 0,
    staleTime: 5 * 60 * 1000,
    refetchInterval: searchQuery ? undefined : 30_000,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
  });

  const chatThreads = useMemo(() => {
    const pages =
      infiniteData?.pages.flatMap((page) => {
        return Array.isArray(page) ? page : page.threads || [];
      }) || [];

    const selectedThreadMatchesUnreadFilter =
      search.unreadOnly !== true || (selectedThreadData?.unread_count ?? 0) > 0;

    if (
      selectedThreadData &&
      search.thread &&
      selectedThreadMatchesUnreadFilter
    ) {
      const hasThread = pages.find((t) => t.id === search.thread);
      if (!hasThread) {
        return [selectedThreadData, ...pages];
      }
      return pages;
    }

    return pages;
  }, [infiniteData, selectedThreadData, search.thread, search.unreadOnly]);

  const handleRefreshThreads = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ["providerChatThreadsInfinite"],
    });
  }, []);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <Chat
      user={providerUser}
      chatThreads={chatThreads}
      searchQuery={searchInput}
      onSearchChange={setSearchInput}
      onRefreshThreads={handleRefreshThreads}
      onLoadMore={handleLoadMore}
      hasMore={hasNextPage}
      isLoading={isLoading || isFetchingNextPage}
      isLoadingSelectedThread={isLoadingSelectedThread}
      unreadOnly={search.unreadOnly === true}
      onUnreadOnlyChange={(value) => {
        router.navigate({
          // @ts-expect-error router search merge keeps existing route params
          search: (prev) => {
            const { thread, ...rest } = prev;
            return {
              ...rest,
              unreadOnly: value || undefined,
            };
          },
        });
      }}
    />
  );
}
